/**
 * ChatDock — the always-visible chat concierge.
 *
 * - Lists/creates sessions
 * - Streams the assistant via SSE
 * - Inline visualization for tool calls (`get_quote`, `get_history`, etc.)
 * - When a tool result arrives we ALSO push state into FocalCard so e.g.
 *   "show NVDA" naturally opens the chart in the main panel.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../lib/api';
import { useStream } from '../lib/useStream';
import { useFocal } from '../lib/focal';
import { useChatPanel } from '../App';
import { ApprovalCard } from './ApprovalCard';
import { SwarmActivity } from './SwarmActivity';

interface Block {
  kind: 'user' | 'text' | 'tool_call' | 'tool_result' | 'error' | 'contrarian' | 'market_signal' | 'decision_card' | 'wizard_link';
  text?: string;
  tool_id?: string;
  tool_name?: string;
  input?: unknown;
  output?: unknown;
  /** wizard_link payload (v0.5.9 AE-113) */
  wizardId?: string;
  /** market_signal payload */
  ms?: {
    subject: string;
    verdict: 'hot' | 'cold' | 'neutral' | string;
    confidence: number;
    headline: string;
    citations: string[];
    components: Record<string, number | null>;
  };
}

// Tool result → action. Returns either a focal state (for modal overlays)
// or a route path (for page navigation), or null.
type ToolAction = { type: 'focal'; state: any } | { type: 'navigate'; path: string } | null;

function actionForTool(name: string, input: any): ToolAction {
  switch (name) {
    case 'get_history':
      return { type: 'focal', state: { kind: 'chart', symbol: String(input?.symbol).toUpperCase() } };
    case 'get_quote': {
      const syms = (input?.symbols ?? []) as string[];
      return syms.length === 1
        ? { type: 'focal', state: { kind: 'chart', symbol: String(syms[0]).toUpperCase() } }
        : null;
    }
    case 'review_security':
    case 'get_market_signal':
      return input?.symbol
        ? { type: 'focal', state: { kind: 'chart', symbol: String(input.symbol).toUpperCase() } }
        : null;
    case 'propose_order':
      return { type: 'focal', state: { kind: 'order', symbol: String(input?.symbol ?? '').toUpperCase() } };
    case 'compare_strategies':
      return { type: 'focal', state: { kind: 'backtest' } };
    case 'get_positions':
    case 'get_pnl':
    case 'get_portfolio':
      return { type: 'navigate', path: '/portfolio' };
    case 'list_strategies':
      return { type: 'navigate', path: '/portfolio' };
    case 'place_order':
      return { type: 'navigate', path: '/approvals' };
    default:
      return null;
  }
}

// v0.5.9 AE-113: wizard tools carry the wizard_id in their OUTPUT (not the
// input), so the deeplink chip is derived from the tool result. Exported
// for the wizard-live-refresh vitest.
export function wizardIdFromToolResult(toolName: string | undefined, output: unknown): string | null {
  if (!toolName) return null;
  if (toolName !== 'propose_thesis' && !toolName.startsWith('thesis_wizard_')) return null;
  if (!output || typeof output !== 'object') return null;
  const o = output as Record<string, unknown>;
  if ('error' in o) return null;
  return typeof o.wizard_id === 'string' && o.wizard_id ? o.wizard_id : null;
}

export function ChatDock() {
  const { set: setFocal } = useFocal();
  const navigate = useNavigate();
  const { pendingMessage, clearPendingMessage } = useChatPanel();
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // bootstrap a session
  useEffect(() => {
    (async () => {
      const sessions = await api.chat.listSessions().catch(() => []);
      let id: number | null;
      if (sessions.length === 0) {
        id = (await api.chat.createSession()).id;
      } else {
        id = sessions[0].id;
        try {
          const msgs = await api.chat.listMessages(id);
          setBlocks(
            msgs.map((m) => ({
              kind: m.role === 'user' ? 'user' : 'text',
              text: (m.content as { text?: string })?.text ?? '',
            })),
          );
        } catch {
          setBlocks([]);
        }
      }
      setSessionId(id);
    })();
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: 999_999, behavior: 'smooth' });
    }
  }, [blocks]);

  // v0.5.4 AE-067: when the Debate button (or any other surface) opens
  // chat with a pre-seeded message via App.useChatPanel.openWithMessage,
  // pull it into the draft and auto-send once the session is bootstrapped.
  // Without this the chat opens empty and the user has to retype the
  // decision headline — the seed message was being silently dropped.
  useEffect(() => {
    if (!pendingMessage) return;
    if (!sessionId) return;
    if (streaming) return;
    setDraft(pendingMessage);
    setBlocks((prev) => [...prev, { kind: 'user', text: pendingMessage }]);
    setStreaming(true);
    clearPendingMessage();
  }, [pendingMessage, sessionId, streaming, clearPendingMessage]);

  const stream = useStream({
    url: streaming && sessionId ? `/api/v1/chat/sessions/${sessionId}/send` : '',
    method: 'POST',
    body: streaming ? { text: draft } : undefined,
    enabled: streaming,
    onEvent: ({ event, data }) => {
      const d = data as Record<string, unknown>;
      if (event === 'text') {
        const delta = String(d.delta ?? '');
        setBlocks((prev) => {
          const last = prev[prev.length - 1];
          if (last?.kind === 'text') {
            return [...prev.slice(0, -1), { ...last, text: (last.text ?? '') + delta }];
          }
          return [...prev, { kind: 'text', text: delta }];
        });
      } else if (event === 'tool_call_start') {
        setBlocks((prev) => [
          ...prev,
          {
            kind: 'tool_call',
            tool_id: String(d.id ?? ''),
            tool_name: String(d.name ?? ''),
            input: d.input,
          },
        ]);
      } else if (event === 'tool_result') {
        setBlocks((prev) => [
          ...prev,
          {
            kind: 'tool_result',
            tool_id: String(d.id ?? ''),
            output: d.output,
          },
        ]);
        // Route tool result to focal overlay or page navigation
        const lastCall = [...blocks].reverse().find((b) => b.kind === 'tool_call' && b.tool_id === d.id);
        if (lastCall?.tool_name) {
          const action = actionForTool(lastCall.tool_name, lastCall.input);
          if (action?.type === 'focal') setFocal(action.state);
          else if (action?.type === 'navigate') navigate(action.path);
          // v0.5.9 AE-113: wizard tools get a deeplink chip into the live
          // wizard, so the user can watch chat-driven authoring in the UI.
          const wid = wizardIdFromToolResult(lastCall.tool_name, d.output);
          if (wid) {
            setBlocks((prev) => {
              const last = prev[prev.length - 1];
              // avoid stacking duplicate chips for the same wizard
              const prior = [...prev].reverse().find((b) => b.kind === 'wizard_link');
              if (prior?.wizardId === wid && last?.kind !== 'user') return prev;
              return [...prev, { kind: 'wizard_link', wizardId: wid }];
            });
          }
        }
      } else if (event === 'contrarian') {
        const t = String(d.text ?? '');
        if (t.trim()) {
          setBlocks((prev) => [...prev, { kind: 'contrarian', text: t }]);
        }
      } else if (event === 'market_signal') {
        // Market Signal track (ADR 0020): the third epistemological lens.
        const ms = d as unknown as Block['ms'];
        if (ms && ms.subject) {
          setBlocks((prev) => [...prev, { kind: 'market_signal', ms }]);
        }
      } else if (event === 'error') {
        setBlocks((prev) => [
          ...prev,
          { kind: 'error', text: String(d.message ?? 'error') },
        ]);
      }
    },
    onClose: () => {
      setStreaming(false);
      setDraft('');
    },
    onError: (err) => {
      setBlocks((prev) => [...prev, { kind: 'error', text: err.message }]);
      setStreaming(false);
    },
  });

  const onSend = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!sessionId || !draft.trim() || streaming) return;
      setBlocks((prev) => [...prev, { kind: 'user', text: draft }]);
      setStreaming(true);
    },
    [sessionId, draft, streaming],
  );

  return (
    <div className="flex-1 flex flex-col h-full">
      <div className="border-b border-edge px-3 py-2 flex items-center justify-between">
        <div className="text-xs text-ink-mute">Meeri · concierge</div>
        <button
          className="btn text-xs"
          onClick={async () => {
            const s = await api.chat.createSession();
            setSessionId(s.id);
            setBlocks([]);
          }}
        >
          new
        </button>
      </div>

      {/* v0.5.9 AE-111: dispatches from ask_specialist are ticketed
          (AE-110), so the user can watch the swarm answer their question. */}
      <div className="px-3 pt-2">
        <SwarmActivity title="swarm" collapsible />
      </div>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 text-sm">
        {blocks.length === 0 && (
          <div className="text-xs text-ink-mute">
            ask me about your portfolio, watchlists, or trades.
          </div>
        )}
        {blocks.map((b, i) => {
          if (b.kind === 'user') {
            return (
              <div key={i} className="flex justify-end">
                <div className="bg-accent text-bg rounded-lg px-3 py-2 max-w-[85%] whitespace-pre-wrap">
                  {b.text}
                </div>
              </div>
            );
          }
          if (b.kind === 'text') {
            return (
              <div key={i} className="text-ink whitespace-pre-wrap break-words overflow-hidden" style={{ overflowWrap: 'anywhere' }}>
                {b.text}
              </div>
            );
          }
          if (b.kind === 'tool_call') {
            return (
              <div key={i} className="text-[11px] text-ink-mute font-mono break-all overflow-hidden">
                ↪ {b.tool_name}({JSON.stringify(b.input)})
              </div>
            );
          }
          if (b.kind === 'tool_result') {
            const out = b.output as Record<string, unknown> | null;
            // Pending approval card
            if (
              out &&
              typeof out === 'object' &&
              out.status === 'pending_approval' &&
              typeof out.client_order_id === 'string'
            ) {
              return (
                <div key={i} className="pl-3 border-l border-warn">
                  <div className="text-[11px] text-ink-mute mb-1 font-mono">
                    pending approval
                  </div>
                  <ApprovalCard order={out as unknown as api.OrderPublic} compact />
                </div>
              );
            }
            // DecisionCard (review_security tool result) — structured render
            if (
              out &&
              typeof out === 'object' &&
              typeof out.subject === 'string' &&
              typeof out.conviction === 'number' &&
              Array.isArray(out.scenarios)
            ) {
              return <DecisionCardBlock key={i} card={out as unknown as DecisionCardPayload} />;
            }
            const summary = summarizeToolOutput(b.output);
            return (
              <div key={i} className="text-[11px] text-ink-dim font-mono pl-3 border-l border-edge">
                {summary}
              </div>
            );
          }
          if (b.kind === 'error') {
            return (
              <div key={i} className="text-bad text-xs">
                ! {b.text}
              </div>
            );
          }
          if (b.kind === 'contrarian') {
            return (
              <div
                key={i}
                className="border-l-4 border-warn pl-3 py-1 text-sm italic text-ink-mute"
                title="The Contrarian (a separate agent) flagged this blind spot."
              >
                <span className="block text-[10px] uppercase tracking-wide text-warn not-italic font-semibold mb-0.5">
                  alternative perspective
                </span>
                {b.text}
              </div>
            );
          }
          if (b.kind === 'market_signal' && b.ms) {
            return <MarketSignalBlock key={i} ms={b.ms} />;
          }
          if (b.kind === 'wizard_link' && b.wizardId) {
            return (
              <button
                key={i}
                type="button"
                data-testid="wizard-deeplink"
                onClick={() => navigate(`/research?wizard=${b.wizardId}`)}
                className="pill text-[11px] border-accent text-accent"
                title="Open the live wizard — watch the swarm work while you author"
              >
                open thesis wizard · {b.wizardId.slice(0, 8)} →
              </button>
            );
          }
          return null;
        })}
        {stream.status === 'open' && (
          <div className="text-xs text-ink-dim animate-pulse">…</div>
        )}
      </div>

      <form onSubmit={onSend} className="border-t border-edge p-3 flex gap-2">
        <input
          className="input flex-1"
          placeholder={streaming ? '' : 'ask Meeri…'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={streaming}
        />
        <button type="submit" className="btn-primary" disabled={streaming || !draft.trim()}>
          send
        </button>
      </form>
    </div>
  );
}

function summarizeToolOutput(o: unknown): string {
  if (o == null) return 'ok';
  if (typeof o === 'string') return o.length > 240 ? o.slice(0, 240) + '…' : o;
  try {
    const s = JSON.stringify(o);
    return s.length > 240 ? s.slice(0, 240) + '…' : s;
  } catch {
    return String(o);
  }
}

// ----- Market Signal block (ADR 0020) -----

function MarketSignalBlock({ ms }: { ms: NonNullable<Block['ms']> }) {
  const tone =
    ms.verdict === 'hot'
      ? 'border-accent text-accent'
      : ms.verdict === 'cold'
      ? 'border-bad text-bad'
      : 'border-edge text-ink-mute';
  return (
    <div
      className={`border-l-4 ${tone} pl-3 py-1 text-sm`}
      title="Market Signal (a separate agent) — pure market behavior, cited percentiles, NOT a recommendation."
    >
      <span className="block text-[10px] uppercase tracking-wide font-semibold mb-0.5">
        market signal · {ms.subject} · {ms.verdict}
      </span>
      <span className="text-ink">{ms.headline.replace(/^market signal: \w+ — ?/i, '')}</span>
    </div>
  );
}

// ----- DecisionCard rendering for review_security tool result -----

interface DecisionCardPayload {
  subject: string;
  conviction: number;
  conviction_rationale: string;
  reasons_for: { track: string; text: string }[];
  reasons_against: { track: string; text: string }[];
  scenarios: { name: string; probability: number; summary: string; suggested_action: string }[];
  disagreement_level: 'low' | 'medium' | 'high' | string;
  disagreement_summary?: string | null;
  what_would_change_my_mind?: string | null;
  timestamp?: string | null;
}

function trackTone(t: string) {
  switch (t) {
    case 'intrinsic':
      return 'text-accent';
    case 'market':
      return 'text-warn';
    case 'contrarian':
      return 'text-bad';
    case 'macro':
      return 'text-ink-mute';
    default:
      return 'text-ink-mute';
  }
}

function DecisionCardBlock({ card }: { card: DecisionCardPayload }) {
  const disTone =
    card.disagreement_level === 'high'
      ? 'text-bad border-bad'
      : card.disagreement_level === 'medium'
      ? 'text-warn border-warn'
      : 'text-ok border-edge';
  return (
    <div className="card p-3 my-2 text-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold">{card.subject} · review</span>
        <span
          className="text-[11px] num"
          title={card.conviction_rationale}
        >
          conviction <span className="font-bold text-base">{card.conviction}/10</span>
        </span>
      </div>
      <div className="text-[11px] text-ink-mute mb-3">{card.conviction_rationale}</div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[11px] text-ok uppercase font-semibold mb-1">why YES</div>
          <ul className="space-y-1">
            {(card.reasons_for ?? []).map((r, i) => (
              <li key={i} className="text-xs">
                <span className={`mr-1 font-mono text-[10px] uppercase ${trackTone(r.track)}`}>
                  {r.track}
                </span>
                <span className="text-ink">{r.text}</span>
              </li>
            ))}
            {(card.reasons_for ?? []).length === 0 && (
              <li className="text-[11px] text-ink-dim italic">none surfaced</li>
            )}
          </ul>
        </div>
        <div>
          <div className="text-[11px] text-bad uppercase font-semibold mb-1">why NO</div>
          <ul className="space-y-1">
            {(card.reasons_against ?? []).map((r, i) => (
              <li key={i} className="text-xs">
                <span className={`mr-1 font-mono text-[10px] uppercase ${trackTone(r.track)}`}>
                  {r.track}
                </span>
                <span className="text-ink">{r.text}</span>
              </li>
            ))}
            {(card.reasons_against ?? []).length === 0 && (
              <li className="text-[11px] text-ink-dim italic">none surfaced</li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-edge pt-2 mb-2">
        <div className="text-[11px] text-ink-mute uppercase font-semibold mb-1">scenarios</div>
        <div className="space-y-1.5">
          {(card.scenarios ?? []).map((s, i) => (
            <div key={i} className="text-xs">
              <span className="font-mono uppercase text-[10px] text-ink-mute mr-1">{s.name}</span>
              <span className="num text-ink-mute mr-2">{Math.round(s.probability * 100)}%</span>
              <span className="text-ink">{s.summary}</span>
              <div className="text-[11px] text-accent ml-12 italic">→ {s.suggested_action}</div>
            </div>
          ))}
        </div>
      </div>

      {card.disagreement_level !== 'low' && (
        <div className={`border-l-2 ${disTone} pl-2 text-[11px] mb-2`}>
          <span className="uppercase font-semibold">disagreement: {card.disagreement_level}</span>
          {card.disagreement_summary && (
            <span className="text-ink-mute"> — {card.disagreement_summary}</span>
          )}
        </div>
      )}

      {card.what_would_change_my_mind && (
        <div className="text-[11px] text-ink-mute italic">
          <span className="uppercase font-semibold not-italic mr-1">what would change my mind:</span>
          {card.what_would_change_my_mind}
        </div>
      )}

      <div className="text-[10px] text-ink-dim mt-2 italic">
        Decision card. By design (ADR 0020) this is NOT a buy/hold/sell — it's the structured
        evidence so you can make the call.
      </div>
    </div>
  );
}
